<script setup>
import { useScheduleControls } from '@/composables/useScheduleControls';
import { useScheduleAction } from '@/composables/useScheduleAction';

const props = defineProps({
    session: { type: Object, required: true },
});

const { showMySchedule, showSeatReservations } = useScheduleControls();
const { busy, addButtonSaved, seatButtonSaved, seatButtonWaitlisted, handleAdd, handleSeat } = useScheduleAction(toRef(props, 'session'));

// Conflict is a MODIFIER here exactly as it is on the agenda cards (Jack,
// 2026-08-20): the panel keeps its own body colour and buttons, and only gains
// the error-red header and border, the "!" and " - Action Needed" appended to
// whatever the header already said.
//
// Resolved through the shared lookup rather than a field on the session: the
// drawer fetches straight from /api/v1/sessions, so it never passes through
// agenda.vue's applyConflicts overlay and the row carries no is_conflicted.
const scheduleConflicts = useScheduleConflicts();
onMounted(() => { scheduleConflicts.ensureConflictIds(); });
const isConflicted = computed(() => (
    scheduleConflicts.isConflicted(props.session.unique_id)
    || props.session.is_conflicted === true
));

const isLimited = computed(() => props.session.session_reservations === true);
const isFull = computed(() => !!props.session.is_full);

// WAITLIST MODE, which this drawer never got. The standby work gave the agenda
// cards a third state — a full session is not a dead end, joining its waitlist
// is a real action — but the drawer only received the "already waitlisted" half.
// So a full session opened from a card offered "Save a Seat" here while the card
// behind it offered "Join Waitlist", and a seat that cannot be saved was the one
// thing on offer (UNB-748, Bri via Marker.io).
//
// Same shape as the cards': already holding a place counts, and so does a full
// limited-capacity session where nothing is held yet. The cards additionally
// consult a `hasSavedSeat` prop they are given; here seatButtonSaved covers it,
// since the drawer resolves its own state from the composable.
//
// `isLimited` in the third clause is BELT-AND-BRACES and a mutation confirmed it:
// removing it fails nothing, because showSeatButton already gates the button on
// exactly that, so this computed is never read for an unlimited session. Kept
// because the condition is the honest statement of when a waitlist exists, and
// it stops this reading as though a full unlimited session had one — but do not
// mistake it for the thing enforcing it. That is showSeatButton.
const seatIsStandby = computed(() =>
    seatButtonWaitlisted.value
    || (isFull.value && !seatButtonSaved.value && isLimited.value)
);

// Variant mirrors the agenda cards: the orange nudge asks the viewer to save a
// seat, so it can't show before seat saving opens — the cards gate this on
// showSeatReservations too. Until then an added limited session reads as plain
// Limited Capacity.
const variant = computed(() => {
    if (!isLimited.value) return 'generic';
    if (showSeatReservations.value && addButtonSaved.value && !seatButtonSaved.value) {
        return isFull.value ? 'full-action-required' : 'action-required';
    }
    return 'limited-capacity';
});
const isOrange = computed(() => variant.value === 'action-required' || variant.value === 'full-action-required');

const downloads = computed(() => {
    const { attachments, attachments_name } = props.session;
    if (!attachments || attachments.length === 0) return [];
    const names = attachments_name ? attachments_name.split(',').map((n) => n.trim()) : [];
    return attachments.map((a, i) => ({ url: a.url, name: names[i] || a.filename || `Attachment ${i + 1}` }));
});

// A full session used to hide this button, on the reasoning that it "cannot be
// reserved, so Save a Seat is a dead action" (Nick, [Agenda] [Session is Full]).
// Bizzabo answers a PUT on a full session with 202 and puts the attendee on its
// standby list — verified against the live API 2026-09-02 — so joining standby
// is a real action and the button stays, reading "Join Waitlist".
const showSeatButton = computed(() => (
    isLimited.value && showSeatReservations.value
));

const hasContent = computed(() => isLimited.value || isConflicted.value || showMySchedule.value || downloads.value.length > 0);

// ── Variant-keyed class maps ────────────────────────────────────────────────
const rootBg = computed(() => (isOrange.value ? 'bg-(--orange)' : 'bg-white'));

// Error red wins over every variant colour — it is the conflict signal.
const headerBg = computed(() => {
    if (isConflicted.value) return 'bg-[#980000]';
    return variant.value === 'full-action-required' ? 'bg-(--dark-pink)' : 'bg-(--magenta)';
});

// Title Case here, not the cards' uppercase — this panel's labels are written
// that way and the CSS uppercases them.
const baseHeaderLabel = computed(() => {
    if (!isLimited.value) return '';
    return variant.value === 'full-action-required' ? 'Session Is Full' : 'Limited Capacity';
});
const headerLabel = computed(() => {
    if (!isConflicted.value) return baseHeaderLabel.value;
    return baseHeaderLabel.value ? `${baseHeaderLabel.value} - Action Needed` : 'Action Needed';
});
const showBang = computed(() => (
    isConflicted.value
    || variant.value === 'action-required'
    || variant.value === 'full-action-required'
));

// The panel gains the same red outline the conflicting cards carry.
const rootBorder = computed(() => (isConflicted.value ? 'border border-[#980000]' : ''));

// Explains the clash and offers the way to see it (Figma 4753:107845). The comp
// only draws the orange-bodied case; on a white body the type takes magenta, the
// same inversion the downloads list below already makes.
// How many OTHER things share this slot — the copy differs between one and
// several (Jack, 2026-08-21).
const conflictPartnerCount = computed(() => (
    props.session.unique_id ? scheduleConflicts.conflictPartners(props.session.unique_id).length : 0
));

// Both variants open with the same sentence (Figma 4753:107845 single,
// 4753:108039 multiple). What differs is the promise at the end, and only the
// single-clash comp makes it: with exactly one other item, removing this session
// really does resolve the overlap. With several, it may not — so the comp drops
// the sentence rather than claiming something untrue.
const CONFLICT_NOTE = 'This session is in the same time slot as other items on My Schedule and requires action.';
const CONFLICT_NOTE_RESOLVES = 'Removing it from My Schedule will resolve the issue.';
const conflictNote = computed(() => (
    conflictPartnerCount.value === 1
        ? `${CONFLICT_NOTE} ${CONFLICT_NOTE_RESOLVES}`
        : CONFLICT_NOTE
));

const conflictNoteCls = computed(() => (isOrange.value ? 'text-white' : 'text-(--magenta)'));
const conflictLinkCls = computed(() => (
    isOrange.value ? 'text-white decoration-white' : 'text-(--magenta) decoration-[#a55c87]'
));

// Same entry point the notification banner uses: `force` bypasses the
// once-per-visit guard and the Save a Seat gate, because this is an explicit ask.
const openConflicts = async () => {
    await scheduleConflicts.maybeShow(async () => {
        await navigateTo({
            path: '/agenda',
            query: { tab: 'myschedule', view: 'calendar', conflicts: 1 },
        });
    }, { force: true });
};

// Added → filled magenta, hovering to orange (the button's action becomes
// "remove", so it takes the compliant orange rather than the magenta it already
// wears). Not added → outline that fills magenta on hover. On an orange card
// body the filled magenta stays put: an orange hover would vanish into it.
const addBtnCls = computed(() => {
    if (isOrange.value) return 'bg-(--magenta) border-(--magenta) text-white';
    return addButtonSaved.value
        ? 'bg-(--magenta) border-(--magenta) text-white hover:bg-(--orange) hover:border-(--orange)'
        : 'bg-transparent border-(--magenta) text-(--magenta) hover:bg-(--magenta) hover:text-white';
});
// A held seat fills the button, matching the add button above and detail-card.
// Without this the button carried the right label ("Seat Saved") on a plain
// outline, which reads as not-yet-saved — the agenda card beside it was filled
// and the tray was not (Nick, Asana 1217708959827782 for the tray and
// 1217708959827789 for the page; one control, since both render this component).
//
// The saved branch sits first for readability, not necessity: `variant` only
// goes orange while the seat is NOT saved, so the two can never both apply.
const seatBtnCls = computed(() => {
    // Hovers to orange like the add button above — the click gives the seat up,
    // so the control signals removal rather than staying the colour it is.
    // A standby place is held too — same removal affordance as a seat.
    if (seatButtonSaved.value || seatButtonWaitlisted.value) return 'bg-(--magenta) border-(--magenta) text-white hover:bg-(--orange) hover:border-(--orange) hover:text-white';
    if (isOrange.value) return 'bg-transparent border-white text-white';
    return 'bg-transparent border-(--magenta) text-(--magenta) hover:bg-(--magenta) hover:text-white';
});
const dividerCls = computed(() => (isOrange.value ? 'border-[rgba(255,255,255,0.25)]' : 'border-[#f3e3ea]'));
const dlLabelCls = computed(() => (isOrange.value ? 'text-white' : 'text-(--darker-pink)'));
const dlLinkCls = computed(() => (isOrange.value ? 'text-white decoration-white' : 'text-(--magenta) decoration-[#a55c87]'));
</script>

<template>
    <div v-if="hasContent" data-test="sidebar-root" class="w-full rounded-[10px] overflow-clip flex flex-col" :class="[rootBg, rootBorder]">
        <!-- Header: limited-capacity / action-required (magenta) / full-action-required (dark-pink) -->
        <div
            v-if="isLimited || isConflicted"
            data-test="sidebar-header"
            class="flex items-start gap-[10px] px-[30px] py-[9px] w-full"
            :class="headerBg"
        >
            <!-- Figma 4753:107838. py-[9px] around a 12px/1.5 (18px) line is
                 exactly the 36px this bar used to hard-code — but as a fixed
                 h-[36px] with py-[14px] it left 8px for an 18px line, so the
                 label was squeezed and a two-line label ("SESSION IS FULL -
                 ACTION NEEDED") had nowhere to wrap to (Jack, 2026-08-21).
                 Height is content-driven now: unchanged at one line, correct at
                 two. items-start keeps the glyph on the FIRST line when it wraps,
                 rather than floating to the middle of the block. -->
            <span v-if="showBang" data-test="sidebar-bang" class="inline-flex items-center justify-center size-[14px] mt-[2px] rounded-full bg-white text-(--magenta) text-[11px] font-bold leading-none shrink-0">!</span>
            <span class="flex-1 min-w-0 font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase text-white">
                {{ headerLabel }}
            </span>
        </div>

        <div v-if="isConflicted || showMySchedule || (showSeatButton) || downloads.length" class="flex flex-col p-[30px] w-full">
            <!-- Figma 4753:107844 — why the card is red, and where to go about it. -->
            <p
                v-if="isConflicted"
                data-test="sidebar-conflict-note"
                class="m-0 pb-[25px] w-full font-hubspot-sans font-light text-[14px] leading-[1.5] tracking-[0.28px]"
                :class="conflictNoteCls"
            >
                {{ conflictNote }}
                <button
                    type="button"
                    data-test="sidebar-conflict-learn-more"
                    class="cursor-pointer border-none bg-transparent p-0 font-[inherit] text-[inherit] leading-[inherit] tracking-[inherit]
                        align-baseline
                        underline decoration-dotted decoration-[8.5%] underline-offset-[3px]
                        transition-[text-decoration] duration-200 hover:no-underline"
                    :class="conflictLinkCls"
                    @click.stop.prevent="openConflicts"
                >Learn More</button>
            </p>
            <div
                v-if="showMySchedule || (showSeatButton)"
                class="flex flex-col gap-[17px] w-full"
                :class="downloads.length ? `border-b ${dividerCls} pb-[25px]` : ''"
            >
                <button
                    v-if="showMySchedule"
                    type="button"
                    data-test="sidebar-add"
                    :aria-label="addButtonSaved ? 'Added to My Schedule' : 'Add to My Schedule'"
                    :disabled="busy"
                    class="group/addbtn flex items-center gap-[8px] w-full border rounded-[50px] px-[14px] py-[8px] cursor-pointer transition-colors"
                    :class="addBtnCls"
                    @click.stop.prevent="handleAdd"
                >
                    <Plus class="size-[11px] shrink-0 transition-transform duration-200 group-hover/addbtn:rotate-45 motion-reduce:transition-none" />
                    <!-- Hovering an added session already turned the button orange
                         here, but the label stayed "Added to My Schedule" while the
                         session cards swapped theirs to the remove wording. Same
                         treatment as detail-card now, so the two read alike. -->
                    <!-- nowrap per Figma. The drawer sidebar is the narrowest place
                         this button appears: 270px leaves 161px for the label, and
                         the old hover label "Remove from Schedule" measured 164.7px,
                         so it wrapped to two lines (Jack, 2026-08-21). "Remove
                         Session" is 115px and clears it everywhere. Note "Added to
                         My Schedule" fits by only 1.5px — check the measurement
                         before lengthening either label. -->
                    <span class="font-hubspot-serif font-normal text-[15px] leading-[1.5] tracking-[0.3px] whitespace-nowrap">
                        <template v-if="addButtonSaved">
                            <span class="group-hover/addbtn:hidden">Added to My Schedule</span>
                            <span class="hidden group-hover/addbtn:inline">Remove Session</span>
                        </template>
                        <template v-else>Add to My Schedule</template>
                    </span>
                </button>

                <button
                    v-if="showSeatButton"
                    type="button"
                    data-test="sidebar-seat"
                    :aria-label="seatButtonWaitlisted ? 'Waitlisted' : seatButtonSaved ? 'Seat Saved' : (seatIsStandby ? 'Join Waitlist' : 'Save a Seat')"
                    class="group/seatbtn flex items-center gap-[8px] w-full border rounded-[50px] px-[14px] py-[8px] cursor-pointer transition-colors"
                    :class="seatBtnCls"
                    @click.stop.prevent="handleSeat"
                >
                    <TimeCircle v-if="seatIsStandby" class="size-[14px] shrink-0" />
                    <Seat v-else class="w-[10.167px] h-[11.611px] shrink-0" />
                    <span class="font-hubspot-serif font-normal text-[15px] leading-[1.5] tracking-[0.3px] whitespace-nowrap">
                        <template v-if="seatButtonWaitlisted">
                            <span class="group-hover/seatbtn:hidden">Waitlisted</span>
                            <span class="hidden group-hover/seatbtn:inline">Remove from Waitlist</span>
                        </template>
                        <template v-else>{{ seatButtonSaved ? 'Seat Saved' : (seatIsStandby ? 'Join Waitlist' : 'Save a Seat') }}</template>
                    </span>
                </button>
            </div>

            <div v-if="downloads.length" data-test="sidebar-downloads" class="flex flex-col gap-[20px] pt-[25px] w-full">
                <p class="font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase m-0" :class="dlLabelCls">
                    Session Downloads:
                </p>
                <div class="flex flex-col gap-[8px] w-full">
                    <a
                        v-for="(d, i) in downloads"
                        :key="i"
                        data-test="sidebar-download"
                        :href="d.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] underline decoration-dotted w-fit"
                        :class="dlLinkCls"
                    >{{ d.name }}</a>
                </div>
            </div>
        </div>
    </div>
</template>
